import * as moment from 'moment';

export interface IKpiModel {
  practice_id: string;
  practice_name: string;
  patients: number;
  gic: number;
  gic_aim: number;
  gic_star: number;
  ra: number;
  ra_aim: number;
  ra_star: number;
  nu: number;
  nu_aim: number;
  nu_star: number;
  created: string;
  updated: string;
  ranked_gap: number;
  ranked_gap_practices: number;
  ranked_ra: number;
  ranked_ra_practices: number;
  ranked_nu: number;
  ranked_nu_practices: number;
}

export class KpiModel {
  practiceId: string;
  practiceName: string;
  patients: number;
  gic: number;
  gicAim: number;
  gicStar: number;
  ra: number;
  raAim: number;
  raStar: number;
  nu: number;
  nuAim: number;
  nuStar: number;
  created: moment.Moment;
  updated: moment.Moment;
  rankedGap: number;
  rankedGapPractices: number;
  rankedRa: number;
  rankedRaPractices: number;
  rankedNu: number;
  rankedNuPractices: number;


  constructor(kpi: IKpiModel) {
    this.practiceId = kpi.practice_id;
    this.practiceName = kpi.practice_name;
    this.patients = kpi.patients;
    this.gic = kpi.gic;
    this.gicAim = kpi.gic_aim;
    this.gicStar = kpi.gic_star;
    this.ra = kpi.ra;
    this.raAim = kpi.ra_aim;
    this.raStar = kpi.ra_star;
    this.nu = kpi.nu;
    this.nuAim = kpi.nu_aim;
    this.nuStar = kpi.nu_star;
    this.created = moment(kpi.created);
    this.updated = moment(kpi.updated);
    this.rankedGap = kpi.ranked_gap;
    this.rankedGapPractices = kpi.ranked_gap_practices;
    this.rankedRa = kpi.ranked_ra;
    this.rankedRaPractices = kpi.ranked_ra_practices;
    this.rankedNu = kpi.ranked_nu;
    this.rankedNuPractices = kpi.ranked_nu_practices;
  }

  static fromObject(kpi: IKpiModel): KpiModel {
    return new KpiModel(kpi);
  }

  static fromObjectArray(kpis: IKpiModel[]): KpiModel[] {
    return kpis.map(KpiModel.fromObject);
  }
}
export interface IQualityScoreCard {
  measure_name?: string;
  measure_code?: string;

  open?: string | number;
  numerator?: string | number;
  denominator?: string | number;

  // desglose MCO
  mco_numerator?: string | number;
  mco_denominator?: string | number;

  app?: string | number;
  claim?: string | number;
  ehr?: string | number;               // a veces viene como "ehr"

  compliance_rate?: string | number;

  bm25th_2star?: string | number;
  bm50th_3star?: string | number;
  bm75th_4star?: string | number;
  bm90th_5star?: string | number;

  hits_to_next_target?: string | number;
  weight?: string | number;
  achieved?: string | number;
  points?: string | number;            // aun no lo retorna el webservice

  // extras opcionales que podrías usar en filtros/etiquetas:
  mco?: string;
  product?: string;
  line_of_business?: string;
  billing_tin?: string;
  location_name?: string;
  display_key?: string | number;
}
export interface IResultQualityScoreCard {
  count: number;
  next: string | null;
  previous: string | null;
  results: IQualityScoreCard[];
}

export interface IResultKpiModel {
  count: number;
  next: string | null;
  previous: string | null;
  results: IKpiModel[];
}

export class ResultKpiModel {
  count: number = 0;
  next: string | null = null;
  previous: string | null = null;
  results: KpiModel[] = [];

  constructor(result?: IResultKpiModel) {
    if (result) {
      this.count = result.count;
      this.next = result.next;
      this.previous = result.previous;
      this.results = ResultKpiModel.fromObjectArray(result.results);
    }
  }

  static fromObject(result: IResultKpiModel): ResultKpiModel {
    return new ResultKpiModel(result);
  }

  static fromObjectArray(results: IKpiModel[]): KpiModel[] {
    return results.map(KpiModel.fromObject);
  }
}
export class QualityScoreCard {
  // contexto de la fila (aun no lo retorna el webservice)
  lob: string;
  mco: string;
  product: string;

  // columna "Measures"
  measure_code: string;
  measure_name: string;

  // abiertos / detalles
  open: number | string;
  numerator: number | string;          // Numerator > SOMOS
  numeratorMco: number | string;       // Numerator > MCO
  denominator: number | string;        // Denominator > SOMOS
  denominatorMco: number | string;     // Denominator > MCO

  // cerrados
  app: number | string;
  claim: number | string;
  emr: number | string;                // normalizamos ehr -> emr

  // tasas y benchmarks
  complianceRate: string;              // "82%" (string formateado)
  bench25: string;                     // "50%"
  bench50: string;                     // "67%"
  bench75: string;                     // "75%"
  bench90: string;                     // "82%"

  // otros
  hitsToNextTarget: number | string;
  weight: number | string;
  achieved: string;                    // "82%"
  points: number | string;             // sin data aun

  constructor(row: IQualityScoreCard) {
    //this.type = row.measure_name ?? row.measure_code ?? '—';
    this.lob     = row.line_of_business ?? DASH;
    this.mco     = row.mco ?? DASH;
    this.product = row.product ?? DASH;

    this.measure_code = row.measure_code ?? DASH;
    this.measure_name = row.measure_name ?? DASH;
    this.open        = toInt(row.open);
    this.numerator   = toInt(row.numerator);
    this.denominator = toInt(row.denominator);

    this.numeratorMco   = toInt(row.mco_numerator);
    this.denominatorMco = toInt(row.mco_denominator);
    this.bench25        = toPct(row.bm25th_2star);

    // Columna sin data del webservice: placeholder igual al de la vista de referencia
    this.points         = toInt(row.points);

    this.app  = toInt(row.app);
    this.claim= toInt(row.claim);
    this.emr  = toInt(row.ehr);                       // mapeo ehr -> emr

    this.complianceRate = toPct(row.compliance_rate);
    this.bench50        = toPct(row.bm50th_3star);
    this.bench75        = toPct(row.bm75th_4star);
    this.bench90        = toPct(row.bm90th_5star);

    this.hitsToNextTarget = toInt(row.hits_to_next_target);
    this.weight           = toFixed(row.weight, 2);
    this.achieved         = toPct(row.achieved);
  }

  static fromObject(row: IQualityScoreCard): QualityScoreCard {
    return new QualityScoreCard(row);
  }

  static fromObjectArray(rows: IQualityScoreCard[]): QualityScoreCard[] {
    return rows.map(QualityScoreCard.fromObject);
  }
}

// === Helpers locales (puedes moverlos a util si prefieres) ===
// Placeholder de la vista de referencia para valores vacios
const DASH = '-';

function toNum(v: any): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function toInt(v: any, fallback: string = DASH) {
  const n = toNum(v);
  return n === null ? fallback : Math.round(n);
}
function toFixed(v: any, d = 2, fallback: string = DASH) {
  const n = toNum(v);
  return n === null ? fallback : Number(n.toFixed(d)).toString();
}
function toPct(v: any, d = 0, fallback: string = DASH) {
  const n = toNum(v);
  return n === null ? fallback : `${(n * 100).toFixed(d)}%`; // 0.82 -> "82%"
}
export class ResultQualityScoreCard {
  count: number = 0;
  next: string | null = null;
  previous: string | null = null;
  results: QualityScoreCard[] = [];

  constructor(result?: IResultQualityScoreCard) {
    if (result) {
      this.count = result.count;
      this.next = result.next;
      this.previous = result.previous;
      this.results = ResultQualityScoreCard.fromObjectArray(result.results);
    }
  }

  static fromObject(result: IResultQualityScoreCard): ResultQualityScoreCard {
    return new ResultQualityScoreCard(result);
  }

  static fromObjectArray(rows: IQualityScoreCard[]): QualityScoreCard[] {
    return rows.map(QualityScoreCard.fromObject);
  }
}
